/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import memoize from 'memoize-one';
import _ from 'lodash';

import styles from './styles.module.scss';
import Sidebar, { SidebarSection } from '../../components/Sidebar';
import MediaSearchQuery from '../../components/MediaSearchQuery';
import ScrollableContent from '../../components/ScrollableContent';
import CoverCard from '../../components/CoverCard';
import SliderList from '../../components/SliderList';
import DataLoader from '../../components/DataLoader';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import IconButton from '../../components/IconButton';
import Aggregation from '../../components/Aggregation';
import actions from '../../redux/actions';
import { getSubmittedSearchParams } from '../../redux/reducers/app';
import { getCategories } from '../../redux/reducers/categories';
import {
  getMediaSearchParams,
  getSubmittedMediaSearchParams,
  getSections as getMediaSearchSections,
  getAggregations as getMediaSearchAggregations,
  getSelectedCategoryIds as getMediaSearchSelectedCategoryIds,
} from '../../redux/reducers/mediaSearch';
import {
  getNewsSearchParams,
  getSubmittedNewsSearchParams,
  getSections as getNewsSections,
  getAggregations as getNewsSearchAggregations,
  getSelectedCategoryIds as getNewsSelectedCategoryIds,
} from '../../redux/reducers/news';
import common from '../../utils/common';
import protonesUtils from '../../utils/protones';
import { abbreviateNumber } from '../../utils/string';
import searchImg from '../../assets/images/search.svg';

import type {
  ReactRouterProps,
  ReduxState,
  Fetchable,
  Category,
  MediaSearchResults,
  MediaSearchFullParams,
  SearchParams,
  SimplifiedMedia,
  Aggregations,
  MediaAggregationKey,
  Translate,
  Track,
} from '../../types';
import { searchParamsProps } from '../../types/SearchParams';

type Props = ReactRouterProps & {
  isNews: boolean,
  params: MediaSearchFullParams,
  submittedParams: MediaSearchFullParams,
  submittedCommonParams: SearchParams,
  categories: Fetchable<Category[]>,
  aggregations: Fetchable<Aggregations<MediaAggregationKey>>,
  sections: Fetchable<MediaSearchResults>,
  pageScroll: number,
  expandedCategories: {
    [categoryId: number]: boolean,
  },
  slideIndexByCategory: {
    [categoryId: number]: number,
  },
  askTracks: typeof actions.askTracks,
  setMediaSearch: typeof actions.setMediaSearch,
  performMediaSearch: typeof actions.performMediaSearch,
  setNewsSearch: typeof actions.setNewsSearch,
  performNewsSearch: typeof actions.performNewsSearch,
  mediaSearchSetPageScroll: typeof actions.mediaSearchSetPageScroll,
  newsSearchSetPageScroll: typeof actions.newsSearchSetPageScroll,
  mediaSearchToggleExpandCategory: typeof actions.mediaSearchToggleExpandCategory,
  newsSearchToggleExpandCategory: typeof actions.newsSearchToggleExpandCategory,
  mediaSearchSetCategorySlide: typeof actions.mediaSearchSetCategorySlide,
  newsSearchSetCategorySlide: typeof actions.newsSearchSetCategorySlide,
  t: Translate,
};

type State = {
  didSearch: boolean,
  tagsSearchText: string,
  slideIndexByCategory: {
    [categoryId: number]: number,
  },
};

const mapStateToProps = (state: ReduxState, ownProps: Props) => {
  const isNews = ownProps.location.pathname.startsWith('/news');
  if (isNews) {
    return {
      isNews,
      aggregations: getNewsSearchAggregations(state),
      params: getNewsSearchParams(state),
      submittedParams: getSubmittedNewsSearchParams(state),
      submittedCommonParams: getSubmittedSearchParams(state),
      categories: getCategories(state),
      sections: getNewsSections(state),
      selectedCategoryIds: getNewsSelectedCategoryIds(state),
      pageScroll: state.news.pageScroll,
      expandedCategories: state.news.expandedCategories,
      slideIndexByCategory: state.news.slideIndexByCategory,
    };
  } else {
    return {
      isNews,
      aggregations: getMediaSearchAggregations(state),
      params: getMediaSearchParams(state),
      submittedParams: getSubmittedMediaSearchParams(state),
      categories: getCategories(state),
      sections: getMediaSearchSections(state),
      selectedCategoryIds: getMediaSearchSelectedCategoryIds(state),
      pageScroll: state.mediaSearch.pageScroll,
      expandedCategories: state.mediaSearch.expandedCategories,
      slideIndexByCategory: state.mediaSearch.slideIndexByCategory,
    };
  }
};

class MediaSearch extends React.Component<Props, State> {
  state = {
    didSearch: false,
    tagsSearchText: '',
    slideIndexByCategory: this.props.slideIndexByCategory,
  };

  scrollContainer = React.createRef();
  pageScroll: number = 0;

  componentDidMount() {
    if (this.scrollContainer.current) {
      this.scrollContainer.current.scrollTop = this.props.pageScroll;
    }
    if (this.props.isNews) {
      if (this.props.sections.data == null) {
        this._performSearch();
      }
    } else if (this.props.categories.data) {
      if (
        !_.isEqual(
          _.pick(this.props.params, searchParamsProps),
          _.pick(this.props.submittedParams, searchParamsProps)
        )
      ) {
        this._performSearch();
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.submittedParams.news === this.props.submittedParams.news &&
      !_.isEqual(prevProps.submittedParams, this.props.submittedParams)
    ) {
      if (this.state.didSearch !== true) {
        this.setState({ didSearch: true });
      }
      this._performSearch();
    }
    if (this.props.isNews && !prevProps.isNews && this.props.sections.data == null) {
      if (this.state.didSearch !== true) {
        this.setState({ didSearch: true });
      }
      this._performSearch();
    }
    if (this.props.isNews !== prevProps.isNews) {
      prevProps.isNews
        ? this.props.newsSearchSetPageScroll(prevProps.pageScroll)
        : this.props.mediaSearchSetPageScroll(prevProps.pageScroll);
    }
  }

  componentWillUnmount() {
    this.props.isNews
      ? this.props.newsSearchSetPageScroll(this.pageScroll)
      : this.props.mediaSearchSetPageScroll(this.pageScroll);
  }

  _handlePageScroll = (event: any) => {
    const element = event.target;
    const pageScroll = element.scrollTop;
    this.pageScroll = pageScroll;
  };

  _handleRefreshMediaSearch = () => {
    if (this.props.categories.data) {
      this._performSearch();
    }
  };

  _performSearch = () => {
    if (this.props.isNews) {
      this.props.performNewsSearch(this.props.params);
    } else {
      this.props.performMediaSearch(this.props.params);
    }
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _setSearch = params => {
    return this.props.isNews
      ? this.props.performNewsSearch(params)
      : this.props.performMediaSearch(params);
  };

  _toggleCategory = (categoryId: number) => {
    const { params } = this.props;
    const categoryIds = common.toggle(params.categoryIds, categoryId);
    const nextParams = {
      ...params,
      categoryIds,
    };
    this._setSearch(nextParams);
  };

  _toggleCategories = () => {
    const { params, categories } = this.props;
    const allCategoryIds = (categories.data || []).map(c => c.id);
    const categoryIds = params.categoryIds || [];
    const isFilterGroupActive = categoryIds && categoryIds.length > 0;
    const nextParams = {
      ...params,
      categoryIds: isFilterGroupActive ? [] : allCategoryIds,
    };
    this._setSearch(nextParams);
  };

  _getIsFilterActive = (filter: MediaAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || !filters[filter].includes(value)) {
      return false;
    }
    return true;
  };

  _addFilterAndSubmit = (filter: MediaAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null) {
      filters = { [filter]: [value] };
    } else if (filters[filter] == null) {
      filters[filter] = [value];
    } else {
      filters[filter] = _.uniq([...filters[filter], value]);
    }
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _removeFilterAndSubmit = (filter: MediaAggregationKey, value: any) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || !filters[filter].includes(value)) {
      return params;
    }
    filters = filters || {};
    filters = {
      ...filters,
      [filter]: (filters[filter] || []).filter(val => val !== value),
    };
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _toggleFilterAndSubmit = (filter: MediaAggregationKey, value: any) => {
    const isFilterActive = this._getIsFilterActive(filter, value);
    if (isFilterActive) {
      return this._removeFilterAndSubmit(filter, value);
    } else {
      return this._addFilterAndSubmit(filter, value);
    }
  };

  _removeFilterGroupAndSubmit = (filter: MediaAggregationKey) => {
    const { params } = this.props;
    let { filters } = params;
    filters = _.omit(filters, filter) || {};
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _addFilterGroupAndSubmit = (filter: MediaAggregationKey) => {
    const { aggregations, params } = this.props;
    const buckets = _.get(aggregations, `data.${filter}.buckets`, []);
    const filterValues = buckets.map(b => b.key);
    const filters = {
      ...(params.filters || {}),
      [filter]: filterValues,
    };
    const nextParams = {
      ...params,
      filters,
    };
    this._setSearch(nextParams);
    return nextParams;
  };

  _getIsFilterGroupActive = (filter: MediaAggregationKey) => {
    const { params } = this.props;
    let { filters } = params;
    if (filters == null || filters[filter] == null || filters[filter].length === 0) {
      return false;
    }
    return true;
  };

  _toggleFilterGroupAndSubmit = (filter: MediaAggregationKey) => {
    const isFilterGroupActive = this._getIsFilterGroupActive(filter);
    if (isFilterGroupActive) {
      return this._removeFilterGroupAndSubmit(filter);
    } else {
      return this._addFilterGroupAndSubmit(filter);
    }
  };

  _increaseFilterGroupPagination = (filter: MediaAggregationKey) => {
    const { params } = this.props;
    const filterPagination = _.get(params, `filtersMeta.${filter}.pagination`, 10);
    const nextParams = _.merge({}, params);
    _.set(nextParams, `filtersMeta.${filter}.pagination`, filterPagination + 10);
    this._setSearch(nextParams);
  };

  _toggleExcludeProductionMusic = () => {
    const { params } = this.props;
    const nextParams = {
      ...params,
      excludeProductionMusic: !params.excludeProductionMusic,
    };
    this._setSearch(nextParams);
  };

  _resetFiltersAndSubmit = () => {
    const { params } = this.props;
    const nextParams = {
      ...params,
      categoryIds: [],
      excludeProductionMusic: false,
      filters: {},
    };
    this.setState({ tagsSearchText: '' });
    this._setSearch(nextParams);
  };

  _renderExcludeProductionMusic = () => {
    const checked = !!this.props.submittedParams.excludeProductionMusic;
    return (
      <div className={styles.aggregation}>
        <div className={styles.aggregationBuckets}>
          <div
            onClick={e => {
              this._toggleExcludeProductionMusic();
              e.preventDefault();
            }}
            className={styles.aggregationBucket}
          >
            <Checkbox
              small={true}
              value={'excludeProductionMusic'}
              label={this.props.t('EXCLUDE_PRODUCTION_MUSIC')}
              labelAfter={true}
              checked={checked}
            />
          </div>
        </div>
      </div>
    );
  };

  _renderAggregations = data => {
    const {
      submittedParams: { categoryIds },
      t,
    } = this.props;
    const { tagsSearchText } = this.state;
    const filters = { ...this.props.submittedParams.filters, categories: categoryIds };
    const {
      categories,
      tags,
      labels,
      performers,
      editors,
      authors,
      years,
      catalogs,
      collectings,
    } = data;
    return (
      <div>
        {this._renderExcludeProductionMusic()}
        <Aggregation
          name="categories"
          label={t('CATEGORIES')}
          categories={this.props.categories.data || []}
          buckets={categories.buckets}
          selectedBuckets={_.get(filters, 'categories', [])}
          onFilterToggle={(_, val) => this._toggleCategory(val)}
          onAllFiltersToggle={this._toggleCategories}
          showAll={true}
        />
        <Aggregation
          name="performers"
          label={t('PERFORMER')}
          buckets={performers.buckets}
          selectedBuckets={_.get(filters, 'performers', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="years"
          label={t('YEAR')}
          buckets={years.buckets}
          selectedBuckets={_.get(filters, 'years', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="labels"
          label={t('LABEL')}
          buckets={labels.buckets}
          selectedBuckets={_.get(filters, 'labels', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="catalogs"
          label={t('CATALOG')}
          buckets={catalogs.buckets}
          selectedBuckets={_.get(filters, 'catalogs', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="authors"
          label={t('AUTHORS')}
          buckets={authors.buckets}
          selectedBuckets={_.get(filters, 'authors', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="editors"
          label={t('EDITORS')}
          buckets={editors.buckets}
          selectedBuckets={_.get(filters, 'editors', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
        <Aggregation
          name="tags"
          label={t('TAGS')}
          buckets={tags.buckets}
          selectedBuckets={_.get(filters, 'tags', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          canSearchFilters={true}
          searchText={tagsSearchText}
          onSearchTextChange={text => this.setState({ tagsSearchText: text })}
        />
        <Aggregation
          name="collectings"
          label={t('COLLECTING')}
          buckets={collectings.buckets}
          selectedBuckets={_.get(filters, 'collectings', [])}
          onFilterToggle={this._toggleFilterAndSubmit}
          onAllFiltersToggle={this._toggleFilterGroupAndSubmit}
          onLoadMore={this._increaseFilterGroupPagination}
        />
      </div>
    );
  };

  _askTracks = (tracks: Track[]) => {
    this.props.askTracks(tracks);
    protonesUtils.goTo(this.props, protonesUtils.routes.ondemand.find());
  };

  _renderNoResults = () => {
    const { t } = this.props;
    return (
      <div className={styles.centeredContent}>
        <div>
          <p align="center">{t('NO_RESULT')}</p>
          <br />
          <Button onClick={() => this._askTracks([])} text={t('WRITE_REQUEST')} />
        </div>
      </div>
    );
  };

  _expandCategory = (categoryId: number) => {
    this.props.isNews
      ? this.props.newsSearchToggleExpandCategory(categoryId)
      : this.props.mediaSearchToggleExpandCategory(categoryId);
  };

  _collapseCategory = (categoryId: number) => {
    this.props.isNews
      ? this.props.newsSearchToggleExpandCategory(categoryId)
      : this.props.mediaSearchToggleExpandCategory(categoryId);
  };

  _handleSlideChange = (categoryId: number, slideIndex: number) => {
    this.props.isNews
      ? this.props.newsSearchSetCategorySlide(categoryId, slideIndex)
      : this.props.mediaSearchSetCategorySlide(categoryId, slideIndex);
  };

  _renderCategory = memoize((category, isCategoryExpanded, data, total) => {
    const { t } = this.props;
    const { slideIndexByCategory } = this.state;
    return data && data.length > 0 ? (
      <div style={{ marginBottom: '3em' }}>
        <SliderList
          key={category.id}
          title={
            <p>
              {category.description}
              <span style={{ marginLeft: '1em' }}>{abbreviateNumber(total)}</span>
            </p>
          }
          render={item => (
            <CoverCard
              title={item.title}
              subtitle={
                item.performers && item.performers.length > 0
                  ? protonesUtils.getPersonLabel(item.performers[0])
                  : t('NO_ARTIST')
              }
              description={[item.year, item.label && item.label.name].filter(_ => _).join(' - ')}
              imgSrc={protonesUtils.getMediaCoverUrl(item)}
              onClick={() => this._handleMediaClick(item)}
            />
          )}
          items={data}
          isExpanded={isCategoryExpanded}
          onExpandClick={() => this._expandCategory(category.id)}
          onCollapseClick={() => this._collapseCategory(category.id)}
          onSlideChange={slideIndex => this._handleSlideChange(category.id, slideIndex)}
          initialSlide={slideIndexByCategory[category.id] || 0}
        />
      </div>
    ) : (
      <div />
    );
  });

  render() {
    const {
      aggregations,
      categories,
      expandedCategories,
      submittedParams,
      sections,
      isNews,
      t,
    } = this.props;
    const { didSearch } = this.state;
    const total = sections.data
      ? _.values(sections.data)
          .map(({ total }) => total || 0) // fix for aggregations fake section
          .reduce((a, b) => a + b, 0)
      : undefined;
    const availableCategories = categories.data || [];
    const gotNoResults: boolean =
      didSearch &&
      !sections.fetching &&
      availableCategories.every(category => {
        const data = _.get(sections, `data[${category.id}]`);
        return data && data.hits && data.hits.length === 0;
      });
    const hasActiveFilters = _.values(submittedParams.filters).some(f => !_.isEmpty(f));
    const canResetFilters =
      (submittedParams.categoryIds || []).length > 0 ||
      hasActiveFilters ||
      !!submittedParams.excludeProductionMusic;
    return (
      <div className={styles.root}>
        <Sidebar
          title={t(isNews ? 'NEWS' : 'SUPPORTS')}
          onRefresh={this._handleRefreshMediaSearch}
          loading={aggregations.fetching || sections.fetching}
        >
          {(isNews || !!submittedParams.text) && (
            <SidebarSection title={t('INFO')}>
              <div className={styles.sidebarSection}>
                {aggregations.fetching || sections.fetching ? (
                  <DataLoader loading={true} />
                ) : (
                  <MediaSearchQuery {...submittedParams} isNews={isNews} total={total} />
                )}
              </div>
            </SidebarSection>
          )}
          {(isNews || !!submittedParams.text) && (
            <SidebarSection
              title={t('FILTERS')}
              disabled={!canResetFilters}
              buttons={[
                <IconButton
                  icon={'reset_filter'}
                  tooltip={t('RESET_FILTERS')}
                  disabled={!canResetFilters}
                  onClick={this._resetFiltersAndSubmit}
                />,
              ]}
            >
              <div className={styles.sidebarSection}>
                <DataLoader
                  loading={aggregations.fetching}
                  error={aggregations.error}
                  data={aggregations.data}
                  render={this._renderAggregations}
                />
              </div>
            </SidebarSection>
          )}
        </Sidebar>
        {isNews || !!submittedParams.text ? (
          <ScrollableContent onScroll={this._handlePageScroll} ref={this.scrollContainer}>
            <div className={styles.main}>
              {sections.fetching ? (
                <DataLoader loading={true} />
              ) : (
                availableCategories.map((category, i) => {
                  const data = _.get(sections, `data[${category.id}]`);
                  const filteredData = data && data.hits;
                  const isCategoryExpanded = !!expandedCategories[category.id];
                  return (
                    <DataLoader
                      key={category.id}
                      loading={sections.fetching}
                      isVisible={!sections.fetching}
                      error={sections.error}
                      data={filteredData}
                      render={filteredData =>
                        this._renderCategory(
                          category,
                          isCategoryExpanded,
                          filteredData,
                          data && data.total
                        )
                      }
                    />
                  );
                })
              )}
              {gotNoResults && !isNews && this._renderNoResults()}
            </div>
          </ScrollableContent>
        ) : (
          <div className={styles.centeredContent}>
            {t('USE_SEARCH_HELPER_1')}{' '}
            <img style={{ padding: '0 0.5em' }} src={searchImg} alt={t('SEARCH')} />{' '}
            {t('USE_SEARCH_HELPER_2')}
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation()(connect(mapStateToProps, actions)(MediaSearch));
